import React, { memo, useCallback, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Text, useTheme } from '@Sage/hoi-poi-ui';
import classNames from 'classnames';

import RichText from 'components/RichText';
import { useWeb5Context } from 'lib/Web5Context';
import { COPILOT_BAR_LOADING_STATES } from 'shards/Copilot/hooks/constants';

import ChatButton from '../ChatButton';
import ClearButton from '../ClearButton';
import VoiceRecording from '../VoiceRecording';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotChatToolbar');

// ========================================
// ACTION TYPES
// ========================================
const VOICE_RECORDING_ACTIONS = {
    START_NEW_SESSION: 'START_NEW_SESSION',
    START_RECORDING: 'START_RECORDING',
    STOP_RECORDING: 'STOP_RECORDING',
    CANCEL_RECORDING: 'CANCEL_RECORDING',
    SEND_RECORDING: 'SEND_RECORDING',
};

// ========================================
// REDUCER
// ========================================
const voiceRecordingReducer = (state, action) => {
    switch (action.type) {
        case VOICE_RECORDING_ACTIONS.START_NEW_SESSION:
            return {
                ...state,
                isRecording: true,
                recordingKey: state.recordingKey + 1,
            };
        case VOICE_RECORDING_ACTIONS.START_RECORDING:
            return {
                ...state,
                isRecording: true,
                showVoiceRecording: true,
            };
        case VOICE_RECORDING_ACTIONS.STOP_RECORDING:
            return {
                ...state,
                isRecording: false,
            };
        case VOICE_RECORDING_ACTIONS.CANCEL_RECORDING:
        case VOICE_RECORDING_ACTIONS.SEND_RECORDING:
            return {
                ...state,
                isRecording: false,
                showVoiceRecording: false,
            };
        default:
            return state;
    }
};

const ChatToolbar = memo(
    ({
        canSubmit: canSubmitProp,
        className,
        conversation,
        enableVoiceInput,
        errorState,
        getQuestionRef,
        initialValue,
        isDanaWritting,
        isLoadingAnswer,
        lastAudioData,
        lastTextMessage,
        onClear,
        onDismissError,
        onRecordingError,
        onSubmit,
        onVoiceSubmit,
        placeholder,
        preComponent,
        topComponent,
    }) => {
        const classes = useStyles();
        const theme = useTheme();
        const { t } = useWeb5Context();

        // ========================================
        // REFS
        // ========================================
        const audioDataRef = useRef(null);
        const stopRecordingRef = useRef(null);
        const shouldSubmitOnCompleteRef = useRef(false);

        // ========================================
        // LOCAL STATE
        // ========================================
        const [voiceRecordingState, dispatch] = useReducer(voiceRecordingReducer, {
            isRecording: false,
            recordingKey: 0,
            showVoiceRecording: false,
        });

        const { isRecording, recordingKey, showVoiceRecording } = voiceRecordingState;
        const [shouldResume, setShouldResume] = useState(false);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const showClearButton = useMemo(() => {
            return (
                Object.values(conversation || {}).filter((item) => item.role === 'user').length > 0
            );
        }, [conversation]);

        const isRecordingError = errorState === COPILOT_BAR_LOADING_STATES.RECORDING_ERROR;
        const isProcessingError = errorState === COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR;
        const hasError = isRecordingError || isProcessingError;

        const errorMessage = useMemo(() => {
            switch (errorState) {
                case COPILOT_BAR_LOADING_STATES.RECORDING_ERROR:
                    return t('label_copilot_bar_recording_error');
                case COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR:
                    return t('label_copilot_bar_processing_error');
                default:
                    return '';
            }
        }, [errorState, t]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const canSubmit = useCallback(
            (editorContent) => {
                // External canSubmit takes precedence if true
                if (canSubmitProp) return true;
                // Default behavior: require text content
                if (!editorContent?.text) return false;
                return true;
            },
            [canSubmitProp],
        );

        const getEditorRef = useCallback((ref) => {
            if (ref?.commands?.focus) ref?.commands?.focus();
        }, []);

        const handleVoiceRecording = useCallback(
            (shouldRecord) => {
                if (!enableVoiceInput) return;

                if (shouldRecord) {
                    // If already in voice recording mode, resume the previous recording
                    if (showVoiceRecording) {
                        setShouldResume(true);
                        dispatch({ type: VOICE_RECORDING_ACTIONS.START_RECORDING });
                    } else {
                        // Clear previous audio data when starting a new session
                        audioDataRef.current = null;
                        setShouldResume(false);
                        dispatch({ type: VOICE_RECORDING_ACTIONS.START_RECORDING });
                    }
                } else {
                    // Stop recording but stay in voice recording mode
                    if (stopRecordingRef.current) {
                        stopRecordingRef.current();
                    }
                    setShouldResume(false);
                    dispatch({ type: VOICE_RECORDING_ACTIONS.STOP_RECORDING });
                }
            },
            [enableVoiceInput, showVoiceRecording],
        );

        const handleVoiceCancel = useCallback(() => {
            audioDataRef.current = null;
            setShouldResume(false);
            shouldSubmitOnCompleteRef.current = false;
            dispatch({ type: VOICE_RECORDING_ACTIONS.CANCEL_RECORDING });
        }, []);

        const handleVoiceSend = useCallback(() => {
            // If recording is active, stop it first and submit when complete
            if (isRecording && stopRecordingRef.current) {
                shouldSubmitOnCompleteRef.current = true;
                stopRecordingRef.current();
                return;
            }

            // If no audio data available, don't submit
            if (!audioDataRef.current) return;

            // Submit the audio
            dispatch({ type: VOICE_RECORDING_ACTIONS.SEND_RECORDING });
            onVoiceSubmit?.(audioDataRef.current);
            audioDataRef.current = null;
            setShouldResume(false);
            shouldSubmitOnCompleteRef.current = false;
        }, [isRecording, onVoiceSubmit]);

        const handleErrorDismiss = useCallback(() => {
            onDismissError?.();
        }, [onDismissError]);

        const handleRetryRecording = useCallback(() => {
            onDismissError?.();
            // Start a new recording session
            audioDataRef.current = null;
            setShouldResume(false);
            dispatch({ type: VOICE_RECORDING_ACTIONS.START_RECORDING });
        }, [onDismissError]);

        const handleRetryProcessing = useCallback(() => {
            onDismissError?.();
            // Re-submit the last message (audio or text)
            if (lastAudioData) {
                onVoiceSubmit?.(lastAudioData);
            } else if (lastTextMessage) {
                onSubmit?.(lastTextMessage);
            }
        }, [lastAudioData, lastTextMessage, onDismissError, onSubmit, onVoiceSubmit]);

        // ========================================
        // MEMOIZED COMPONENT PROPS
        // ========================================
        const handleRecordingComplete = useCallback(
            (audioData) => {
                audioDataRef.current = audioData;

                // If send was requested while recording, submit now
                if (shouldSubmitOnCompleteRef.current && audioData) {
                    shouldSubmitOnCompleteRef.current = false;
                    dispatch({ type: VOICE_RECORDING_ACTIONS.SEND_RECORDING });
                    onVoiceSubmit?.(audioData);
                    audioDataRef.current = null;
                    setShouldResume(false);
                }
            },
            [onVoiceSubmit],
        );

        const voiceRecordingProps = useMemo(
            () => ({
                onError: onRecordingError,
                onRecordingComplete: handleRecordingComplete,
                shouldResume,
                stopRecordingRef,
            }),
            [handleRecordingComplete, onRecordingError, shouldResume],
        );

        const richTextProps = useMemo(
            () => ({
                autofocus: !isLoadingAnswer,
                canSubmit,
                chatbox: true,
                getEditorRef,
                isBasic: true,
                isFullWidth: true,
                isSubmitDisabled: false,
                loading: isDanaWritting,
                onChange: (value) => getQuestionRef?.(value.text),
                onSubmit,
                overrides: {
                    editor: {
                        style: {
                            maxHeight: 120,
                            overflow: 'hidden',
                        },
                    },
                    editorWrapper: {
                        style: {
                            backgroundColor: theme.colors.background.utility025,
                        },
                    },
                    topComponent: {
                        style: {
                            backgroundColor: theme.colors.background.utility025,
                        },
                    },
                },
                placeholder: placeholder || t('placeholder_dana_chat_input'),
                preComponent,
                topComponent,
                toolbar: [],
                value: initialValue || '',
            }),
            [
                canSubmit,
                getEditorRef,
                getQuestionRef,
                initialValue,
                isDanaWritting,
                isLoadingAnswer,
                onSubmit,
                placeholder,
                preComponent,
                topComponent,
                t,
                theme.colors.background.utility025,
            ],
        );

        // ========================================
        // RENDER
        // ========================================
        const chatButtons = useMemo(() => {
            const buttons = [];
            if (!enableVoiceInput) return buttons;

            const iconColor = theme.colors.actionMajor[500];

            if (hasError) {
                // Error state buttons

                // Retry button based on error type
                if (isRecordingError) {
                    buttons.push({
                        hasGlow: true,
                        iconColor,
                        iconName: 'mic',
                        key: 'retryRecording',
                        onClick: handleRetryRecording,
                        tooltip: t('label_speak'),
                    });
                } else if (isProcessingError) {
                    buttons.push({
                        hasGlow: true,
                        iconColor,
                        iconName: 'refresh',
                        key: 'retryProcessing',
                        onClick: handleRetryProcessing,
                        tooltip: t('action_dana_retry_continue'),
                    });
                }

                // Cancel button (dismiss error)
                buttons.push({
                    iconName: 'close',
                    key: 'dismissError',
                    onClick: handleErrorDismiss,
                    tooltip: t('action_cancel'),
                });
            } else {
                // Normal voice recording buttons
                if (showVoiceRecording) {
                    buttons.push({
                        iconName: 'delete',
                        key: 'cancelRecording',
                        onClick: handleVoiceCancel,
                        tooltip: t('action_cancel'),
                    });
                }

                if (!isRecording) {
                    buttons.push({
                        hasGlow: true,
                        iconColor,
                        iconName: 'mic',
                        isDisabled: isDanaWritting || isLoadingAnswer,
                        key: 'voiceRecording',
                        onClick: () => handleVoiceRecording(true),
                        tooltip: t('label_speak'),
                    });
                }

                if (showVoiceRecording && isRecording) {
                    buttons.push({
                        iconName: 'pause',
                        key: 'pauseRecording',
                        onClick: () => handleVoiceRecording(false),
                        tooltip: t('action_pause_recording'),
                    });
                }

                if (showVoiceRecording) {
                    buttons.push({
                        iconColor,
                        iconName: 'send',
                        key: 'sendRecording',
                        onClick: handleVoiceSend,
                        tooltip: t('label_send'),
                    });
                }
            }

            return (
                <div className={classes.chatButtons}>
                    {buttons.map(({ key, ...buttonProps }) => (
                        <ChatButton key={key} {...buttonProps} />
                    ))}
                </div>
            );
        }, [
            classes.chatButtons,
            enableVoiceInput,
            handleErrorDismiss,
            handleRetryProcessing,
            handleRetryRecording,
            handleVoiceCancel,
            handleVoiceRecording,
            handleVoiceSend,
            hasError,
            isDanaWritting,
            isLoadingAnswer,
            isProcessingError,
            isRecording,
            isRecordingError,
            showVoiceRecording,
            t,
            theme.colors.actionMajor,
        ]);

        const content = useMemo(() => {
            switch (true) {
                case hasError:
                    return (
                        <div className={classes.errorContainer}>
                            <Text color={theme.colors.textLight.primary} type="caption">
                                {errorMessage}
                            </Text>
                        </div>
                    );
                case showVoiceRecording:
                    return <VoiceRecording key={recordingKey} {...voiceRecordingProps} />;
                default:
                    return <RichText {...richTextProps} />;
            }
        }, [
            classes.errorContainer,
            errorMessage,
            hasError,
            recordingKey,
            richTextProps,
            showVoiceRecording,
            theme.colors.textLight.primary,
            voiceRecordingProps,
        ]);

        return (
            <div
                className={classNames(classes.root, className, {
                    [classes.hasUserMessages]: showClearButton,
                })}
            >
                {showClearButton && !showVoiceRecording && !hasError && (
                    <ClearButton onClick={!isDanaWritting && !isLoadingAnswer && onClear} />
                )}

                {content}
                {chatButtons}
            </div>
        );
    },
);

ChatToolbar.displayName = 'ChatToolbar';

ChatToolbar.propTypes = {
    canSubmit: PropTypes.bool,
    className: PropTypes.string,
    conversation: PropTypes.object,
    enableVoiceInput: PropTypes.bool,
    errorState: PropTypes.oneOf([
        COPILOT_BAR_LOADING_STATES.RECORDING_ERROR,
        COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR,
        null,
    ]),
    getQuestionRef: PropTypes.func,
    initialValue: PropTypes.string,
    isDanaWritting: PropTypes.bool,
    isLoadingAnswer: PropTypes.bool,
    lastAudioData: PropTypes.object,
    lastTextMessage: PropTypes.string,
    onClear: PropTypes.func,
    onDismissError: PropTypes.func,
    onRecordingError: PropTypes.func,
    onSubmit: PropTypes.func,
    onVoiceSubmit: PropTypes.func,
    placeholder: PropTypes.string,
    preComponent: PropTypes.node,
    topComponent: PropTypes.node,
};

export default ChatToolbar;
